import React, { useState } from 'react';
import {
  Check,
  CreditCard,
  Camera,
  Droplets,
  FileText,
  Key,
  Layers,
  Receipt,
  Users,
  Wrench,
  Zap
} from 'lucide-react';
import Footer from './Footer';

const FeaturesPage = ({ showPage }) => {
  const [activeFeatureTab, setActiveFeatureTab] = useState('payments');

  return (
    <div className="page active">
      <section className="pt-16 pb-12 bg-gradient-to-br from-primary-50 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-6">
            <Layers className="w-3 h-3" /> All Features
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-neutral-900 mb-4">Built for modern living</h1>
          <p className="text-neutral-600 max-w-xl mx-auto mb-10">Every tool you need to manage your home or property, in one powerful platform.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['payments', 'electricity', 'maintenance', 'community', 'access', 'documents'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveFeatureTab(tab)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  activeFeatureTab === tab
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Payments Tab */}
          {activeFeatureTab === 'payments' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-6">
                  <CreditCard className="w-3 h-3" /> Payments
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Pay rent the way you want</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">No more checks, no more portals that crash on the 1st. Bilm makes rent payments instant, secure, and flexible.</p>
                <div className="space-y-4">
                  {[
                    'Bank transfer, card & USSD',
                    'Autopay scheduling',
                    'Instant receipts'
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-neutral-900 text-sm">{feature}</div>
                        <div className="text-neutral-500 text-sm">Multiple payment methods supported</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-primary-100 rounded-large p-8 flex items-center justify-center" style={{minHeight: 380}}>
                <div className="w-full max-w-sm bg-white rounded-large shadow-custom p-6 space-y-4">
                  <h3 className="font-heading font-bold text-neutral-900">Pay Rent</h3>
                  <div className="text-4xl font-extrabold font-heading text-neutral-900">₦85,000<span className="text-xl text-neutral-400">.00</span></div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Base Rent</span>
                      <span className="font-medium text-neutral-900">₦80,000</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Service Charge</span>
                      <span className="font-medium text-neutral-900">₦5,000</span>
                    </div>
                    <div className="border-t border-neutral-100 pt-2 flex justify-between text-sm font-bold">
                      <span>Total</span>
                      <span>₦85,000</span>
                    </div>
                  </div>
                  <button className="w-full py-3 bg-primary-600 text-white font-semibold rounded-small text-sm hover:bg-primary-700 transition-colors">
                    Pay ₦85,000
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Electricity Tab */}
          {activeFeatureTab === 'electricity' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-6">
                  <Zap className="w-3 h-3" /> Electricity
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Power at your fingertips</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">Buy prepaid electricity tokens instantly. Works with all major Nigerian DISCOs.</p>
                <div className="space-y-4">
                  {[
                    'All Nigerian DISCOs supported',
                    'Instant token delivery'
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-neutral-900 text-sm">{feature}</div>
                        <div className="text-neutral-500 text-sm">EKEDC, IKEDC, AEDC, PHEDC and more</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-large p-6 text-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-heading font-bold text-lg">Buy Electricity</span>
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="bg-white/20 rounded-large p-3 mb-4">
                    <div className="text-xs text-amber-100 mb-1">Meter Number</div>
                    <div className="font-heading font-bold text-lg">45110012345</div>
                    <div className="text-xs text-amber-100 mt-1">Unit 402 • EKEDC</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {['₦1k', '₦2k', '₦5k', '₦10k'].map((amount) => (
                      <button key={amount} className="py-2 text-sm font-bold rounded-small bg-white text-amber-700">
                        {amount}
                      </button>
                    ))}
                  </div>
                  <button className="w-full py-3 bg-white text-amber-600 font-bold rounded-small text-sm">
                    Buy Now — ₦1,000
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Tab */}
          {activeFeatureTab === 'maintenance' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold mb-6">
                  <Wrench className="w-3 h-3" /> Maintenance
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Fix it fast, track it all</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">Submit maintenance requests in seconds with photos. Track progress in real time.</p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 text-sm">Photo & video attachments</div>
                      <div className="text-neutral-500 text-sm">Show exactly what needs fixing</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-large p-8 flex items-center justify-center" style={{minHeight: 380}}>
                <div className="w-full max-w-sm bg-white rounded-large shadow-custom p-6 space-y-4">
                  <h3 className="font-heading font-bold text-neutral-900">New Request</h3>
                  <div className="space-y-3">
                    <div className="p-3 border border-neutral-200 rounded-small flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center">
                        <Droplets className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-neutral-700">Plumbing Issue</span>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-small text-sm text-neutral-500">
                      Describe issue...
                    </div>
                    <div className="border-2 border-dashed border-neutral-200 rounded-small p-4 text-center text-sm text-neutral-400">
                      <Camera className="w-5 h-5 mx-auto mb-1" /> Add Photos
                    </div>
                  </div>
                  <button className="w-full py-3 bg-green-600 text-white font-semibold rounded-small text-sm">
                    Submit Request
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Community Tab */}
          {activeFeatureTab === 'community' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-6">
                  <Users className="w-3 h-3" /> Community
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Connect with your neighbors</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">Build a real sense of community. Share updates, join events, and stay connected.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-large p-8 flex items-center justify-center" style={{minHeight: 380}}>
                <div className="w-full max-w-sm space-y-3">
                  <div className="bg-white rounded-large shadow-custom p-4 border border-neutral-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-xs">M</div>
                      <div>
                        <div className="text-sm font-semibold text-neutral-900">Management</div>
                        <div className="text-xs text-neutral-400">2 min ago</div>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600">Pool maintenance scheduled for Saturday 8am–12pm.</p>
                  </div>
                  <div className="bg-white rounded-large shadow-custom p-4 border border-neutral-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-xs">J</div>
                      <div>
                        <div className="text-sm font-semibold text-neutral-900">James (Unit 301)</div>
                        <div className="text-xs text-neutral-400">1 hr ago</div>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600">Anyone interested in a rooftop BBQ this weekend? 🔥</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Access Tab */}
          {activeFeatureTab === 'access' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-6">
                  <Key className="w-3 h-3" /> Access & Security
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Your home, secured</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">Digital keys, visitor management, and real-time security alerts — all in your pocket.</p>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-large p-8 flex items-center justify-center" style={{minHeight: 380}}>
                <div className="w-full max-w-sm bg-white rounded-large shadow-custom p-6 text-center space-y-4">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
                    <Key className="w-10 h-10 text-teal-600" />
                  </div>
                  <h3 className="font-heading font-bold text-neutral-900">Unit 402</h3>
                  <div className="w-full py-4 bg-teal-600 text-white font-bold rounded-large text-lg">
                    🔓 Unlocked
                  </div>
                  <p className="text-xs text-neutral-400">Tap to lock your unit</p>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeFeatureTab === 'documents' && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-6">
                  <FileText className="w-3 h-3" /> Documents
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">All your docs, one place</h2>
                <p className="text-neutral-600 leading-relaxed mb-8">Access your lease, rent receipts, electricity token history, and more — anytime, anywhere.</p>
              </div>
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-large p-8 flex items-center justify-center" style={{minHeight: 380}}>
                <div className="w-full max-w-sm bg-white rounded-large shadow-custom p-6 space-y-3">
                  <h3 className="font-heading font-bold text-neutral-900 mb-4">My Documents</h3>
                  {[
                    { icon: FileText, title: 'Lease Agreement', desc: 'Signed Jan 2024 • PDF', color: 'blue' },
                    { icon: Receipt, title: 'Rent Receipts', desc: '12 receipts', color: 'green' }
                  ].map((doc) => (
                    <div key={doc.title} className="flex items-center gap-3 p-3 border border-neutral-100 rounded-large hover:bg-neutral-50 cursor-pointer">
                      <div className={`w-9 h-9 bg-${doc.color}-100 text-${doc.color}-600 rounded-large flex items-center justify-center`}>
                        <doc.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-neutral-900">{doc.title}</div>
                        <div className="text-xs text-neutral-400">{doc.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
        </div>
      </section>

      <Footer showPage={showPage} />
    </div>
  );
};

export default FeaturesPage;
